// src/features/admin/SettingsTab.tsx
// Platform-level admin settings: audit, maintenance, notifications, federation,
// cross-platform analytics. GetAdminSettings, UpdateAdminSettings.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { adminStore } from '@core/stores';
import { Button, Input, ErrorAlert } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { StringListEditor } from './StringListEditor';
import type { AdminSettings } from '@core/types/admin';
import { errMessage } from './logic';

const DEFAULTS: AdminSettings = {
  audit_enabled: true,
  audit_retention_days: 365,
  maintenance_mode: false,
  maintenance_message: '',
  allowed_ips_during_maintenance: [],
  admin_notification_emails: [],
  notify_on_new_tenant: true,
  notify_on_security_event: true,
  federation_auto_accept: false,
  trusted_registry_urls: [],
  federation_sync_interval_minutes: 60,
  federation_require_dpa: true,
  enable_cross_platform_analytics: false,
  share_anonymized_stats: false,
};

export const SettingsTab = observer(() => {
  const { t } = useI18n();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminStore.GetAdminSettings()
      .then((res) => setSettings(res.settings ?? DEFAULTS))
      .catch((e: unknown) => setError(errMessage(e, t('admin.settingsTab.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  const patch = (partial: Partial<AdminSettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...partial } : prev));
    setSaved(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminStore.UpdateAdminSettings({ settings });
      setSettings(res.settings ?? settings);
      setSaved(true);
    } catch (e) {
      setError(errMessage(e, t('admin.settingsTab.saveError')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (!settings) return null;

  return (
    <div className="max-w-2xl space-y-6">
      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {saved && <p className="text-sm text-success">{t('admin.settingsTab.saved')}</p>}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('admin.settingsTab.section.audit')}</h3>
        <ToggleSwitch label={t('admin.settingsTab.auditEnabled')} checked={settings.audit_enabled} onChange={(v) => patch({ audit_enabled: v })} />
        <Input
          type="number"
          label={t('admin.settingsTab.auditRetentionDays')}
          value={settings.audit_retention_days}
          onChange={(e) => patch({ audit_retention_days: e.target.value === '' ? 0 : Number(e.target.value) })}
          className="w-40"
          fullWidth={false}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('admin.settingsTab.section.maintenance')}</h3>
        <ToggleSwitch label={t('admin.settingsTab.maintenanceMode')} checked={settings.maintenance_mode} onChange={(v) => patch({ maintenance_mode: v })} />
        <Input
          label={t('admin.settingsTab.maintenanceMessage')}
          value={settings.maintenance_message}
          onChange={(e) => patch({ maintenance_message: e.target.value })}
        />
        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('admin.settingsTab.allowedIps')}</p>
          <StringListEditor
            value={settings.allowed_ips_during_maintenance}
            onChange={(v) => patch({ allowed_ips_during_maintenance: v })}
            addLabel={t('common.add')}
            emptyLabel={t('admin.settingsTab.allowedIpsEmpty')}
            placeholder="203.0.113.0/24"
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('admin.settingsTab.section.notifications')}</h3>
        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('admin.settingsTab.notificationEmails')}</p>
          <StringListEditor
            value={settings.admin_notification_emails}
            onChange={(v) => patch({ admin_notification_emails: v })}
            addLabel={t('common.add')}
            emptyLabel={t('admin.settingsTab.notificationEmailsEmpty')}
            placeholder="admin@example.com"
          />
        </div>
        <ToggleSwitch label={t('admin.settingsTab.notifyOnNewTenant')} checked={settings.notify_on_new_tenant} onChange={(v) => patch({ notify_on_new_tenant: v })} />
        <ToggleSwitch label={t('admin.settingsTab.notifyOnSecurityEvent')} checked={settings.notify_on_security_event} onChange={(v) => patch({ notify_on_security_event: v })} />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('admin.settingsTab.section.federation')}</h3>
        <ToggleSwitch label={t('admin.settingsTab.federationAutoAccept')} checked={settings.federation_auto_accept} onChange={(v) => patch({ federation_auto_accept: v })} />
        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('admin.settingsTab.trustedRegistries')}</p>
          <StringListEditor
            value={settings.trusted_registry_urls}
            onChange={(v) => patch({ trusted_registry_urls: v })}
            addLabel={t('common.add')}
            emptyLabel={t('admin.settingsTab.trustedRegistriesEmpty')}
            placeholder="https://registry.example.com"
          />
        </div>
        <Input
          type="number"
          label={t('admin.settingsTab.federationSyncInterval')}
          value={settings.federation_sync_interval_minutes}
          onChange={(e) => patch({ federation_sync_interval_minutes: e.target.value === '' ? 0 : Number(e.target.value) })}
          className="w-40"
          fullWidth={false}
        />
        <ToggleSwitch label={t('admin.settingsTab.federationRequireDpa')} checked={settings.federation_require_dpa} onChange={(v) => patch({ federation_require_dpa: v })} />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t('admin.settingsTab.section.analytics')}</h3>
        <ToggleSwitch label={t('admin.settingsTab.enableCrossPlatformAnalytics')} checked={settings.enable_cross_platform_analytics} onChange={(v) => patch({ enable_cross_platform_analytics: v })} />
        <ToggleSwitch label={t('admin.settingsTab.shareAnonymizedStats')} checked={settings.share_anonymized_stats} onChange={(v) => patch({ share_anonymized_stats: v })} />
      </section>

      <Button onClick={save} isLoading={saving}>{t('admin.settingsTab.save')}</Button>
    </div>
  );
});

export default SettingsTab;
